<template>
  <div :id="id" ref="kebab" v-click-outside="() => isOpen = false" class="c-kebab-menu" :tabindex="kebabTabIndex" @mouseover="openBy === 'hover' && openByHover()" @click.stop @focus="($evt) => handleKebabFocus($evt)" @blur="($evt) => handleKebabBlur($evt)" @keydown="($evt) => handleKeydown($evt)">
    <div class="c-kebab-menu__icon">
      <VpIcon icon="kebab" :style="{'font-size': size, 'color': iconColor, 'margin': iconMargin }" />
    </div>
    <div :id="id+'Actions'" class="c-kebab-menu__bubble" :class="[{ 'is-open': isOpen }, positionClasses]">
      <ul class="c-kebab-menu__list">
        <li
          v-for="(option, index) in options"
          :key="index"
          :ref="'option' + index"
          :title="option.hoverTitle"
          tabindex="-1"
          class="c-kebab-menu__list-item"
          :class="[{'is-disabled': option.isDisabled === true},{'has-focus': index === focusIndex }]"
          @click="handleOptionClick(option)"
          @keydown.enter="handleOptionEnter(option)"
          @keydown.space="handleOptionEnter(option)"
          @blur="handleMenuItemBlur()"
          @focus="handleMenuItemFocus(index)"
          @mousemove="handleMouseMove(option,index)"
        >
          {{ option.label }}
        </li>
      </ul>
      <i></i>
    </div>
  </div>
</template>

<script>
import VpIcon from '../vp-icon/VpIcon.vue';
import { clickOutside } from '../../utilities/directives';

export default {
  name: 'VpKebabMenu',
  components: {
    VpIcon,
  },
  directives: {
    clickOutside,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    item: {
      type: Object,
      default: () => {},
    },
    menuOptions: {
      type: Array,
      default: () => [],
    },
    menuPosition: {
      type: String,
      default: 'down-right',
    },
    openBy: {
      type: String,
      default: 'hover',
      validator(value) {
        return ['hover', 'click'].indexOf(value) !== -1;
      },
    },
    size: {
      type: String,
      default: '20px',
    },
    iconColor: {
      type: String,
      default: '#4F4F4F',
    },
    iconMargin: {
      type: String,
      default: '0 0 0 0',
    }
  },
  emits: ['click'],
  data: function data() {
    return {
      focusIndex: '',
      isOpen: false,
      pendingFocusLoss: false,
    };
  },
  computed: {
    kebabTabIndex() {
      return this.isOpen ? -1 : 0;
    },
    positionClasses() {
      return {
        'c-kebab-menu__bubble--down-right': this.menuPosition === 'down-right' || !this.menuPosition,
        'c-kebab-menu__bubble--down-left': this.menuPosition === 'down-left',
        'c-kebab-menu__bubble--up-right': this.menuPosition === 'up-right',
        'c-kebab-menu__bubble--up-left': this.menuPosition === 'up-left',
      };
    },
    options() {
      return this.menuOptions;
    },
  },
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.focusIndex = '';
      }
    }
  },
  methods: {
    focusOnItem(index) {
      this.focusIndex = index;
      this.$refs[`option${this.focusIndex}`][0].focus();
    },
    handleKebabBlur(event) {
      // don't close the menu if the kebab icon lost focus to one of the child menu options
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.pendingFocusLoss = true;
        setTimeout(() => {
          if (this.pendingFocusLoss) {
            this.isOpen = false;
            this.pendingFocusLoss = false;
          }
        }, 100);
      }
    },
    handleMenuItemBlur() {
      this.pendingFocusLoss = true;
      setTimeout(() => {
        if (this.pendingFocusLoss) {
          this.isOpen = false;
          this.pendingFocusLoss = false;
        }
      }, 100);
    },
    handleKebabFocus() {
      this.isOpen = true;
      this.pendingFocusLoss = false;
    },
    handleMenuItemFocus(index) {
      this.pendingFocusLoss = false;
      /** if toggled by element.focus event, I.E VpTable.Keyboard.Navigation */
      return (index !== null) ? this.focusOnItem(index) : null;
    },
    handleMouseMove(option, index) {
      this.focusOnItem(index);
    },
    handleKeydown(event) {
      if (event.key === 'ArrowDown') {
        this.focusOnNextItem();
        event.preventDefault();
      }
      if (event.key === 'ArrowUp') {
        this.focusOnPrevItem();
        event.preventDefault();
      }
    },
    handleOptionClick(option) {
      if (!option.isDisabled) {
        this.$emit('click', option);
        this.isOpen = false;
      }
    },
    handleOptionEnter(option) {
      this.$emit('click', option);
      this.isOpen = false;
    },
    focusOnNextItem(index) {
      let startIndex = index;
      if (startIndex === undefined) {
        if (this.focusIndex === '') {
          this.focusIndex = 0;
          startIndex = 0;
        } else {
          startIndex = (this.focusIndex + 1);
        }
      }
      let found = false;
      for (let i = startIndex; i < this.menuOptions.length; i += 1) {
        if (!this.$refs[`option${i}`][0].classList.contains('is-disabled')) {
          this.$refs[`option${i}`][0].focus();
          this.focusIndex = i;
          found = true;
          break;
        }
      }
      if (!found) {
        this.focusOnNextItem(0);
      }
    },
    focusOnPrevItem(index) {
      let startIndex = index;
      if (startIndex === undefined) {
        if (this.focusIndex === '') {
          this.focusIndex = (this.menuOptions.length - 1);
          startIndex = this.focusIndex;
        } else {
          startIndex = this.focusIndex - 1;
        }
      }
      let found = false;
      for (let i = startIndex; i >= 0; i -= 1) {
        if (!this.$refs[`option${i}`][0].classList.contains('is-disabled')) {
          this.$refs[`option${i}`][0].focus();
          this.focusIndex = i;
          found = true;
          break;
        }
      }
      if (!found) {
        const lastIndex = (this.menuOptions.length - 1);
        this.focusOnPrevItem(lastIndex);
      }
    },
    openByHover() {
      this.isOpen = true;
    },
  }
};
</script>

<style lang="scss" scoped>
@import '../../styles/colors.scss';

.c-kebab-menu {
 $self: &;
 cursor: pointer;
 display: inline-block;
 position: relative;
 &__icon {
   padding: 0;
   margin: 0;
 }
 &__bubble {
    margin: 0;
    padding: 10px 0;
    background-color:#fff;
    border: 1px solid #DBDBDB;
    border-radius:4px;
    box-shadow: 0 1px 8px 0px 1px 4px rgba(0, 0, 0, 0.1);
    display: none;
    position: absolute;
    z-index: 2;
    min-width: 100%;
    &.is-open {
      display: block;
    }
    #{ $self }__list {
      list-style:none;
      margin: 0;
      padding: 0;
      min-width: 100%;
      position: relative;
      #{ $self }__list-item {
        text-decoration: none;
        font-size:18px;
        line-height:42px;
        padding: 0 16px;
        color: #016699;
        user-select: none;
        text-align: left;
        white-space: nowrap;
        display: block;
        &.has-focus {
          background-color: #016699;
          color: #fff;
        }
        &:not(:last-child) {
          border-bottom: 1px solid #F4F7F8;
        }
        &.is-disabled {
          color: #DBDBDB;
          background-color:#fff;
        }
      }
    }
    &--down-right, &--down-left {
      top: 42px;
      i {
        position: absolute;
        bottom: 100%;
        margin-left: -12px;
        width: 24px;
        height: 12px;
        overflow: hidden;
        &:after {
          content:'';
          position:absolute;
          width:12px;
          height:12px;
          left:50%;
          transform:translate(-50%,50%) rotate(45deg);
          background-color: $white;
          border:1px solid #DBDBDB;box-shadow:0 1px 8px 0px 1px 4px rgba(0, 0, 0, 0.1);
        }
      }
    }
    &--up-right, &--up-left {
      bottom: 42px;
      i {
        position: absolute;
        top: 100%;
        margin-left: -12px;
        width: 24px;
        height: 12px;
        overflow: hidden;
        &:after {
          content:'';
          position:absolute;
          width:12px;
          height:12px;
          left:50%;
          transform:translate(-50%,-50%) rotate(45deg);
          background-color: $white;
          border:1px solid #DBDBDB;box-shadow:0 1px 8px 0px 1px 4px rgba(0, 0, 0, 0.1);
        }
      }
    }
    &--down-right, &--up-right {
      left: -36px;
      i {
        left: 46px;
      }
    }
    &--down-left, &--up-left {
      right: -33px;
      i {
        right: 31px;
      }
    }
 }
 &.is-hover:hover {
   #{ $self }__bubble {
     display: block;
   }
 }
}
</style>
